import { useState, useEffect } from 'react'
import { Info } from '@phosphor-icons/react'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'react-i18next'

import TextField from '../../../../FormLegacy/FormFieldsV2/TextField'
import { daysOfWeek } from '../../../../../helpers/constants'
import { onChangeNumberField } from '../helpers'
import { dayToTimestamp, timestampToDay } from '../../../../../helpers/helpers'

const MultidayPricingSelector = ({ values, errors, setFieldValue = () => {}, resetKey }) => {
  const { t } = useTranslation(['reservations'])
  const [selectedDays, setSelectedDays] = useState([])
  const [daySchedules, setDaySchedules] = useState({})
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (resetKey !== undefined && resetKey > 0) {
      setSelectedDays([])
      setDaySchedules({})
      setIsInitialized(false)
    }
  }, [resetKey])

  // Initialize state from existing multidaySlots values
  useEffect(() => {
    const repeatedSlots = values.multidaySlots.filter((slot) => slot.repeats)
    if (repeatedSlots?.length > 0 && !isInitialized) {
      const newSelectedDays = []
      const newDaySchedules = {}

      repeatedSlots.forEach((slot) => {
        const day = timestampToDay(slot.startDate)
        if (day && !newSelectedDays.includes(day)) {
          newSelectedDays.push(day)
          newDaySchedules[day] = {
            price: slot.price?.toString() || '',
          }
        }
      })

      if (newSelectedDays.length > 0) {
        setSelectedDays(newSelectedDays)
        setDaySchedules(newDaySchedules)
        setIsInitialized(true)
      }
    } else if (!repeatedSlots || repeatedSlots.length === 0) {
      // If no multidaySlots are provided, mark as initialized to prevent infinite checking
      setIsInitialized(true)
    }
  }, [values.multidaySlots, isInitialized])

  // Function to update multidaySlots based on daySchedules
  const updateMultidaySlots = () => {
    const multidaySlots = []

    if (selectedDays.length > 0) {
      // Group days by price to create slots
      const priceGroups = {}

      selectedDays.forEach((day) => {
        const schedule = daySchedules[day]
        if (schedule) {
          const price = schedule.price
          if (!priceGroups[price]) {
            priceGroups[price] = []
          }
          priceGroups[price].push(day)
        }
      })

      // Create slots for each price group
      Object.entries(priceGroups).forEach(([price, days]) => {
        days.forEach((day) => {
          multidaySlots.push({
            startDate: dayToTimestamp(day),
            price: price ? parseFloat(price) : null,
            repeats: true,
            weekdays: [daysOfWeek.indexOf(day)],
          })
        })
      })
    }

    const specialPricingSlots = (values.multidaySlots || []).filter(
      (slot) => slot.repeats === false,
    )
    const updatedSlots = [...specialPricingSlots, ...multidaySlots]
    const sortedUpdatedSlots = updatedSlots.sort((a, b) => a.startDate - b.startDate)
    const sortedExistingSlots = (values.multidaySlots || []).sort(
      (a, b) => a.startDate - b.startDate,
    )

    // check if items truly changed
    if (!isEqual(sortedUpdatedSlots, sortedExistingSlots)) {
      setFieldValue('multidaySlots', updatedSlots)
    }

    const newDaysAvailable = selectedDays.map((day) => daysOfWeek.indexOf(day))
    const sortedNewDays = [...newDaysAvailable].sort((a, b) => a - b)
    const sortedExistingDays = [...(values.daysAvailable || [])].sort((a, b) => a - b)
    if (!isEqual(sortedNewDays, sortedExistingDays)) {
      setFieldValue('daysAvailable', newDaysAvailable)
    }
  }

  // Update multidaySlots whenever selectedDays or daySchedules change (but not during initialization)
  useEffect(() => {
    if (isInitialized) {
      updateMultidaySlots()
    }
  }, [selectedDays, daySchedules, isInitialized])

  const handleSelectDay = (day) => {
    const isActive = selectedDays.includes(day)
    if (isActive) {
      setSelectedDays(selectedDays.filter((selectedDay) => selectedDay !== day))
      const newDaySchedules = { ...daySchedules }
      delete newDaySchedules[day]
      setDaySchedules(newDaySchedules)
    } else {
      setSelectedDays([...selectedDays, day])
      setDaySchedules({
        ...daySchedules,
        [day]: {
          price: '',
        },
      })
    }
  }

  const handlePriceChange = (day, value) => {
    setDaySchedules({
      ...daySchedules,
      [day]: {
        ...daySchedules[day],
        price: value,
      },
    })
  }

  return (
    <div className="pricing-container">
      <div className="form-step__label">
        {t('reservations:amenityForms.pricingSelectors.pricingRequired')}
      </div>
      <div className="pricing-container__info">
        <Info size={16} />
        <span>{t('reservations:amenityForms.pricingSelectors.clickWeekdayToggle')}</span>
      </div>

      <div className="pricing-container__weekdays">
        {daysOfWeek.map((day) => {
          const isActive = selectedDays.includes(day)
          return (
            <div key={day} className="weekday-row">
              <button
                type="button"
                className={`weekday-button ${
                  isActive ? 'weekday-button--active' : 'weekday-button--inactive'
                }`}
                onClick={() => handleSelectDay(day)}
              >
                {day}
              </button>
              {isActive ? (
                <div className="weekday-schedule">
                  <div className="price-field">
                    <TextField
                      name={`${day}-price`}
                      placeholder={t(
                        'reservations:amenityForms.pricingSelectors.enterPricePerDayPlaceholder',
                      )}
                      min={0}
                      value={daySchedules[day]?.price || ''}
                      onChange={(e) =>
                        onChangeNumberField(e, (e) => handlePriceChange(day, e.target.value))
                      }
                      type="number"
                      isError={!!errors.multidaySlots}
                    />
                    <span className="price-prefix">$</span>
                  </div>
                </div>
              ) : (
                <div className="weekday-unavailable">
                  <span>{t('reservations:amenityForms.pricingSelectors.unavailable')}</span>
                </div>
              )}
            </div>
          )
        })}
      </div>
      {errors.multidaySlots && <div className="form-step__error">{errors.multidaySlots}</div>}
    </div>
  )
}

export default MultidayPricingSelector
